import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import {
  FiAlertTriangle, FiRefreshCw, FiUser, FiMail,
  FiDollarSign, FiPackage, FiTrendingUp, FiShield,
  FiX, FiCheckCircle, FiFilter, FiSearch
} from 'react-icons/fi';

function AdminFrauds() {
  const [frauds, setFrauds] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadFrauds();
  }, []);

  const loadFrauds = async () => {
    try {
      setLoading(true);
      const response = await api.detectFrauds();
      if (response && response.success) {
        setFrauds(response.data.frauds || []);
        setSummary(response.data.summary || null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des fraudes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-500/20 border-red-500/30 text-red-400';
      case 'medium':
        return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400';
      case 'low':
        return 'bg-blue-500/20 border-blue-500/30 text-blue-400';
      default:
        return 'bg-gray-500/20 border-gray-500/30 text-gray-400';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high':
        return 'Élevé';
      case 'medium':
        return 'Moyen';
      case 'low':
        return 'Faible';
      default:
        return severity;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'DUPLICATE_EMAIL':
        return <FiMail className="w-5 h-5" />;
      case 'SUSPICIOUS_ACTIVITY':
        return <FiTrendingUp className="w-5 h-5" />;
      case 'SUSPICIOUS_PAYMENT':
        return <FiDollarSign className="w-5 h-5" />;
      case 'SPAM_QUOTES':
        return <FiPackage className="w-5 h-5" />;
      case 'BATCH_ACCOUNTS':
        return <FiUser className="w-5 h-5" />;
      default:
        return <FiAlertTriangle className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'DUPLICATE_EMAIL': 'Email dupliqué',
      'SUSPICIOUS_ACTIVITY': 'Activité suspecte',
      'SUSPICIOUS_PAYMENT': 'Paiement suspect',
      'SPAM_QUOTES': 'Spam de devis',
      'BATCH_ACCOUNTS': 'Comptes en masse'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFrauds = frauds.filter(fraud => {
    if (filter !== 'all' && fraud.severity !== filter) return false;
    if (searchQuery && !fraud.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !fraud.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">Détection de fraudes</h1>
          <p className="text-gray-400">Surveillance et détection des activités suspectes</p>
        </div>
        <button
          onClick={loadFrauds}
          disabled={loading}
          className="mt-4 sm:mt-0 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center disabled:opacity-50"
        >
          <FiRefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-400">Total</p>
              <FiAlertTriangle className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold text-yellow-400">{summary.total}</p>
          </div>
          <div className="bg-gray-800 border border-red-600/30 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-400">Risque élevé</p>
              <FiShield className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-3xl font-bold text-red-400">{summary.high}</p>
          </div>
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-400">Risque moyen</p>
              <FiShield className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold text-yellow-400">{summary.medium}</p>
          </div>
          <div className="bg-gray-800 border border-blue-600/30 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-400">Risque faible</p>
              <FiShield className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-blue-400">{summary.low}</p>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher dans les fraudes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
          >
            <option value="all">Tous les niveaux</option>
            <option value="high">Risque élevé</option>
            <option value="medium">Risque moyen</option>
            <option value="low">Risque faible</option>
          </select>
        </div>
      </div>

      {/* Liste des fraudes */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Analyse en cours...</p>
          </div>
        ) : filteredFrauds.length === 0 ? (
          <div className="p-12 text-center">
            <FiShield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Aucune fraude détectée</p>
            <p className="text-gray-500 text-sm mt-2">Tout semble normal pour le moment</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {filteredFrauds.map((fraud) => (
              <div
                key={fraud.id}
                className="p-6 hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-3 rounded-lg border ${getSeverityColor(fraud.severity)}`}>
                      {getTypeIcon(fraud.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-bold text-white">{fraud.title}</h3>
                        <span className={`px-3 py-1 text-xs rounded-full border ${getSeverityColor(fraud.severity)}`}>
                          {getSeverityLabel(fraud.severity)}
                        </span>
                        <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
                          {getTypeLabel(fraud.type)}
                        </span>
                      </div>
                      <p className="text-gray-300 mb-4">{fraud.description}</p>
                      
                      {/* Métriques */}
                      {fraud.metrics && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-900/50 rounded-lg">
                          {Object.entries(fraud.metrics).map(([key, value]) => (
                            <div key={key}>
                              <p className="text-xs text-gray-400 mb-1">{key}</p>
                              <p className="text-sm font-semibold text-white">
                                {typeof value === 'number' && key.includes('Amount')
                                  ? `${value.toFixed(2)} TND`
                                  : typeof value === 'number' && key.includes('Rate')
                                  ? `${value}%`
                                  : value}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Utilisateurs concernés */}
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-400">Utilisateurs concernés:</p>
                        <div className="flex flex-wrap gap-2">
                          {fraud.users.map((user) => (
                            <Link
                              key={user.id}
                              to={`/admin/users/${user.id}`}
                              className="inline-flex items-center px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm"
                            >
                              <FiUser className="w-4 h-4 mr-2 text-yellow-400" />
                              <span className="text-white">{user.name || user.email}</span>
                              <span className="ml-2 text-gray-400 text-xs">{user.email}</span>
                            </Link>
                          ))}
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 mt-4">
                        Détecté le {formatDate(fraud.detectedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminFrauds;


